/**
 * App root: providers + router.
 *
 * Provider stack (outside to inside):
 *  ClerkProvider — auth (Clerk's React SDK)
 *  QueryClientProvider — react-query for backend calls
 *  ApiClientProvider — typed wrapper that adds Clerk JWT to every request
 *  BrowserRouter — react-router-dom v7
 *
 * Routes:
 *  /login         — Clerk SignIn
 *  /onboarding    — multi-step wizard (Spec Section 5)
 *  /dashboard     — main metric view
 *  /products      — placeholder (Phase 1.6+)
 *  /cohorts       — placeholder (Phase 1.6+)
 *  /connections   — placeholder (Phase 1.6+)
 *  /settings      — placeholder (Phase 1.6+)
 */

import { useEffect, useRef, useState } from "react";
import { ClerkProvider, RedirectToSignIn, useAuth } from "@clerk/clerk-react";
import { QueryClient, QueryClientProvider, useQuery, useIsFetching } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { env, isClerkConfigured } from "./lib/env.js";
import { ApiClientProvider, DemoApiClientProvider, useApiClient } from "./lib/api-context.js";
import { ThemeProvider } from "./lib/theme.js";
import AppLayout from "./layouts/AppLayout.js";
import DemoLayout from "./layouts/DemoLayout.js";
import LoginPage from "./pages/LoginPage.js";
import SignUpPage from "./pages/SignUpPage.js";
import InvitePage from "./pages/InvitePage.js";
import CreateAccountPage from "./pages/CreateAccountPage.js";
import DashboardPage from "./pages/DashboardPage.js";
import OnboardingPage from "./pages/OnboardingPage.js";
import GuidedOnboardingPage from "./pages/GuidedOnboardingPage.js";
import ConnectionsPage from "./pages/ConnectionsPage.js";
import MarketingPage from "./pages/MarketingPage.js";
import OperationsPage from "./pages/OperationsPage.js";
import FinancialsPage from "./pages/FinancialsPage.js";
import CostsPage from "./pages/CostsPage.js";
import CohortsPage from "./pages/CohortsPage.js";
import RetentionPage from "./pages/RetentionPage.js";
import SubscriptionsPage from "./pages/SubscriptionsPage.js";
import ProductsPage from "./pages/ProductsPage.js";
import OnboardingChatPage from "./pages/OnboardingChatPage.js";
import SettingsPage from "./pages/SettingsPage.js";
import AdminLayout from "./layouts/AdminLayout.js";
import AdminPage from "./pages/AdminPage.js";
import AgencyPortfolioPage from "./pages/AgencyPortfolioPage.js";
import AgencyIssuesPage from "./pages/AgencyIssuesPage.js";
import AgencyDataHealthPage from "./pages/AgencyDataHealthPage.js";
import AgencyFeedbackPage from "./pages/AgencyFeedbackPage.js";
import AgencyCardsPage from "./pages/AgencyCardsPage.js";
import CmWizardPage from "./pages/CmWizardPage.js";
import CmWizardDocumentPage from "./pages/CmWizardDocumentPage.js";
import CmWizardSpreadsheetPage from "./pages/CmWizardSpreadsheetPage.js";
import NotConfiguredPage from "./pages/NotConfiguredPage.js";
import OverheadReviewPage from "./pages/OverheadReviewPage.js";
import TestOnboardingPage from "./pages/TestOnboardingPage.js";
import { ToastProvider } from "./lib/toast.js";
import { getExpenseSetupGateDecision, getVerificationGateDecision } from "./lib/onboarding-verification-gate.js";
import { rootLandingPath } from "./lib/account-setup-routing.js";
import AppAccessRoutes from "./lib/app-access-routes.js";
import EcomosHandoffPage, { EcomosHandoffUnavailablePage } from "./pages/EcomosHandoffPage.js";
import type { Capabilities } from "./lib/card-requirements.js";
import {
  authQueryCacheScope,
  commitAuthQueryCacheScope,
  type AuthQueryCacheScope,
} from "./lib/auth-query-cache.js";
import { AccountContextBoundary } from "./lib/account-context.js";
import { clearTenantBrowserTransientState } from "./lib/account-browser-state.js";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      // 429 (rate-limit): ONE retry, but delayed by the server's Retry-After (see
      // retryDelay) so the burst that caused it has cleared first — never an
      // immediate retry, which would just feed the burst. Other 4xx (400/401/404)
      // aren't fixable by retrying, so don't. Keep one retry for transient 5xx /
      // network blips.
      retry: (failureCount, error) => {
        const status = (error as { status?: number } | null)?.status;
        if (status === 429) return failureCount < 1;
        if (typeof status === "number" && status >= 400 && status < 500) return false;
        return failureCount < 1;
      },
      retryDelay: (attempt, error) => {
        const retryAfterMs = (error as { retryAfterMs?: number } | null)?.retryAfterMs;
        if (retryAfterMs !== undefined) return Math.min(retryAfterMs, 10_000);
        return Math.min(1000 * 2 ** attempt, 3000);
      },
    },
  },
});

/**
 * A slim indeterminate progress bar pinned to the very top while ANY query is
 * fetching (founder 2026-07-07: "add some loading symbols so they know things
 * are loading"). Delayed ~180ms so instant cached responses never flash it —
 * it only appears when a load actually takes a beat.
 */
function GlobalLoadingBar(): React.ReactElement | null {
  const fetching = useIsFetching();
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (fetching > 0) {
      const t = setTimeout(() => setShow(true), 180);
      return () => clearTimeout(t);
    }
    setShow(false);
    return undefined;
  }, [fetching]);
  if (!show) return null;
  return (
    <div aria-hidden style={{ position: "fixed", top: 0, left: 0, right: 0, height: 3, zIndex: 10000, overflow: "hidden", pointerEvents: "none" }}>
      <div className="dd-indeterminate" style={{ position: "absolute", top: 0, height: "100%", background: "var(--accent)", borderRadius: 2 }} />
    </div>
  );
}

export default function App(): React.ReactElement {
  if (!isClerkConfigured()) {
    return <NotConfiguredPage />;
  }
  return (
    <ClerkProvider publishableKey={env.clerkPublishableKey ?? ""}>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <AuthQueryCacheBoundary>
            <GlobalLoadingBar />
            <ApiClientProvider>
              <BrowserRouter>
                <Routes>
                  <Route path="/login/*" element={<LoginPage />} />
                  <Route path="/signup/*" element={<SignUpPage />} />
                  <Route path="/invite/*" element={<InvitePage />} />
                  <Route path="/create-account" element={<AccountContextBoundary requireBinding={false}><CreateAccountPage /></AccountContextBoundary>} />
                  {/* Public, no-login demo — read-only Vital Roots data. */}
                  <Route path="/demo/*" element={<DemoApp />} />
                  {/* Public anonymous click-through for reviewing the onboarding UX.
                      It never calls the API or writes data. */}
                  <Route path="/onboarding-preview" element={<OnboardingExperiencePreview />} />
                  <Route path="/*" element={<ClerkAppAccessRoutes />} />
                </Routes>
              </BrowserRouter>
            </ApiClientProvider>
          </AuthQueryCacheBoundary>
        </QueryClientProvider>
      </ThemeProvider>
    </ClerkProvider>
  );
}

/**
 * The QueryClient is intentionally shared across routes, but never across
 * Clerk identities. Children stay unmounted until the current user/session (or
 * signed-out state) has cleared and committed its cache scope.
 */
function AuthQueryCacheBoundary({ children }: { children: React.ReactNode }): React.ReactElement | null {
  const { isLoaded, isSignedIn, userId, sessionId } = useAuth();
  const scope = authQueryCacheScope({ isLoaded, isSignedIn, userId, sessionId });
  const committedScope = useRef<AuthQueryCacheScope | null | undefined>(undefined);
  const [readyScope, setReadyScope] = useState<AuthQueryCacheScope | null | undefined>(undefined);

  useEffect(() => {
    const commit = commitAuthQueryCacheScope(
      queryClient,
      committedScope.current,
      scope,
      clearTenantBrowserTransientState,
    );
    committedScope.current = commit.scope;
    setReadyScope(commit.ready ? commit.scope : null);
  }, [scope]);

  if (scope === null || readyScope !== scope) return null;
  return <>{children}</>;
}

function OnboardingExperiencePreview(): React.ReactElement {
  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", color: "var(--text)" }}>
      <div style={{ height: 54, boxSizing: "border-box", padding: "0 22px", display: "flex", alignItems: "center", borderBottom: "1px solid var(--border)", background: "var(--surface)", fontWeight: 750, fontSize: 13 }}>
        Scale Advisors <span style={{ color: "var(--body)", fontWeight: 500, marginLeft: 8 }}>· Profit Intelligence</span>
      </div>
      <ToastProvider>
        <div style={{ padding: "24px clamp(16px, 4vw, 54px) 60px" }}>
          <GuidedOnboardingPage forcePreview />
        </div>
      </ToastProvider>
    </div>
  );
}

/** Resolve Clerk once, then hand the current auth state to the tested route boundary. */
function ClerkAppAccessRoutes(): React.ReactElement | null {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) return null;

  const routes = (
    <AppAccessRoutes
      isSignedIn={isSignedIn === true}
      signedInRoot={<LandingGate />}
      signedInPrivate={<AppRoutes />}
      signedOutPrivate={<RedirectToSignIn />}
    />
  );
  return isSignedIn === true
    ? <AccountContextBoundary>{routes}</AccountContextBoundary>
    : routes;
}

/**
 * Public demo (/demo/*) — no Clerk gate. The DemoApiClientProvider feeds the
 * reused page components read-only Vital Roots data. Locked to the analytics
 * pages; everything else redirects to the Exec view.
 */
function DemoApp(): React.ReactElement {
  return (
    <DemoApiClientProvider>
      <Routes>
        <Route element={<DemoLayout />}>
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="marketing" element={<MarketingPage />} />
          <Route path="products" element={<ProductsPage />} />
          <Route path="cohorts" element={<CohortsPage />} />
          <Route path="retention" element={<RetentionPage />} />
          <Route path="subscriptions" element={<SubscriptionsPage />} />
          <Route path="operations" element={<OperationsPage />} />
          <Route path="financials" element={<FinancialsPage />} />
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="*" element={<Navigate to="dashboard" replace />} />
        </Route>
      </Routes>
    </DemoApiClientProvider>
  );
}

function AppRoutes(): React.ReactElement {
  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route path="/welcome" element={<OnboardingPage />} />
        <Route element={<VerifiedNumbersGate />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          {/* Profit Model now lives as a section inside Exec — redirect the old standalone path. */}
          <Route path="/profit-model" element={<Navigate to="/dashboard" replace />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/marketing" element={<MarketingPage />} />
          <Route path="/operations" element={<OperationsPage />} />
          <Route path="/financials" element={<FinancialsPage />} />
          <Route path="/cohorts" element={<CohortsPage />} />
          <Route path="/retention" element={<RetentionPage />} />
          {/* Geography dissolved into Marketing (2026-06-12 IA decision) — keep old links working. */}
          <Route path="/geography" element={<Navigate to="/marketing" replace />} />
          <Route path="/subscriptions" element={<SubscriptionsPage />} />
        </Route>
        <Route path="/connections" element={<ConnectionsPage />} />
        <Route path="/connections/quickbooks-disconnected" element={<ConnectionsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/integrations/ecomos/open/:grantId" element={<EcomosHandoffPage />} />
        <Route path="/integrations/ecomos/unavailable" element={<EcomosHandoffUnavailablePage />} />
        <Route element={<ExpenseSetupGate />}>
          <Route path="/costs" element={<CostsPage />} />
          <Route path="/costs/overhead" element={<OverheadReviewPage />} />
          {/* Old organizer + form costs pages retired — product setup is the conversation. */}
          <Route path="/catalog" element={<Navigate to="/onboarding/chat" replace />} />
          <Route path="/catalog/costs" element={<Navigate to="/onboarding/chat" replace />} />
          <Route path="/onboarding/chat" element={<OnboardingChatPage />} />
          <Route path="/cm-wizard" element={<CmWizardPage />} />
          <Route path="/cm-wizard/document" element={<CmWizardDocumentPage />} />
          <Route path="/cm-wizard/spreadsheet" element={<CmWizardSpreadsheetPage />} />
        </Route>
      </Route>
      {/* Agency console — the operator's OWN app shell (founder 2026-07-05:
          "a full new area for admin"). Separate nav, no client chrome. */}
      <Route element={<AdminLayout />}>
        <Route path="/agency" element={<AgencyPortfolioPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/agency/issues" element={<AgencyIssuesPage />} />
        <Route path="/agency/data-health" element={<AgencyDataHealthPage />} />
        <Route path="/agency/cards" element={<AgencyCardsPage />} />
        <Route path="/agency/feedback" element={<AgencyFeedbackPage />} />
        <Route path="/agency/test-onboarding" element={<TestOnboardingPage />} />
      </Route>
      <Route path="/onboarding" element={<Navigate to="/welcome" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

/** Gross analytics open once the server confirms account basics and a proven
 * order source. Expense verification remains separate and does not gate the
 * first revenue / Gross LTV view. */
function VerifiedNumbersGate(): React.ReactElement {
  const api = useApiClient();
  const {
    data: agency,
    isLoading: agencyLoading,
    isFetching: agencyFetching,
    isError: agencyError,
    refetch: refetchAgency,
  } = useQuery({
    queryKey: ["account", "agency-status"],
    queryFn: () => api.get<{ isOperator: boolean }>("/account/agency-status"),
    staleTime: 5 * 60_000,
  });
  const {
    data: setup,
    isLoading: setupLoading,
    isFetching: setupFetching,
    isError: setupError,
    refetch: refetchSetup,
  } = useQuery({
    queryKey: ["onboarding", "state"],
    queryFn: () => api.get<{ previewAllowed: boolean }>("/onboarding/state"),
  });
  const decision = getVerificationGateDecision({
    agencyLoading,
    setupLoading,
    agencyError,
    setupError,
    agency,
    setup,
  });
  if (decision === "loading") return <p style={{ color: "var(--body)", padding: 24 }}>Loading…</p>;
  if (decision === "error") {
    return (
      <div role="alert" style={{ maxWidth: 620, border: "1px solid var(--border)", background: "var(--surface)", borderRadius: "var(--r-card)", boxShadow: "var(--shadow-card)", padding: 24 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>We couldn&apos;t confirm your dashboard is ready</h2>
        <p style={{ color: "var(--body)", lineHeight: 1.5 }}>Your business numbers are still locked. Check again in a moment.</p>
        <button
          type="button"
          disabled={agencyFetching || setupFetching}
          onClick={() => { void Promise.all([refetchAgency(), refetchSetup()]); }}
          style={{ border: "none", borderRadius: 8, background: "var(--accent)", color: "var(--accent-contrast)", padding: "9px 14px", fontFamily: "inherit", fontWeight: 700, cursor: agencyFetching || setupFetching ? "wait" : "pointer", opacity: agencyFetching || setupFetching ? 0.65 : 1 }}
        >
          {agencyFetching || setupFetching ? "Checking…" : "Check again"}
        </button>
      </div>
    );
  }
  if (decision === "allow") return <Outlet />;
  return <Navigate to="/welcome?step=4" replace />;
}

/** Keeps the parked expense pass out of the client experience until the whole
 * financial model is ready. Operators retain access to finish and review it. */
function ExpenseSetupGate(): React.ReactElement {
  const api = useApiClient();
  const agency = useQuery({
    queryKey: ["account", "agency-status"],
    queryFn: () => api.get<{ isOperator: boolean }>("/account/agency-status"),
    staleTime: 5 * 60_000,
  });
  const capabilities = useQuery({
    queryKey: ["capabilities"],
    queryFn: () => api.get<Capabilities>("/dashboard/capabilities"),
  });
  const decision = getExpenseSetupGateDecision({
    agencyLoading: agency.isLoading,
    capabilitiesLoading: capabilities.isLoading,
    agencyError: agency.isError,
    capabilitiesError: capabilities.isError,
    agency: agency.data,
    capabilities: capabilities.data,
  });
  if (decision === "loading") return <p style={{ color: "var(--body)", padding: 24 }}>Loading…</p>;
  if (decision === "allow") return <Outlet />;
  return <Navigate to="/cohorts" replace />;
}

/**
 * Decides where a signed-in user lands. New/incomplete accounts go to the
 * onboarding checklist; accounts with a proven order feed start at Customer LTV.
 * Without this, first-login users hit an empty dashboard and never see
 * onboarding (it's only reachable by typing /onboarding by hand).
 *
 * Reads onboarding state fresh (derived server-side from real data, no
 * separate flag to drift). Fails open to Customer LTV if state can't load;
 * the page's own source gate still hides numbers that are not ready.
 */
function LandingGate(): React.ReactElement {
  const api = useApiClient();
  // Operators land on THEIR portfolio, not inside whichever client account
  // happens to be active (founder 2026-07-05: "I should have my own
  // dashboard, then go into an account"). Same cached check the sidebar uses.
  const { data: agency, isLoading: agencyLoading } = useQuery({
    queryKey: ["account", "agency-status"],
    queryFn: () => api.get<{ isOperator: boolean }>("/account/agency-status"),
    staleTime: 5 * 60_000,
  });
  const { data, isLoading, isError } = useQuery({
    queryKey: ["onboarding", "state"],
    queryFn: () => api.get<{ completed: boolean; previewAllowed: boolean }>("/onboarding/state"),
  });
  // Once the operator check resolves true, the active client's setup state no
  // longer decides the root landing. Account switching still goes directly to
  // /dashboard so the operator can deliberately work inside a client account.
  if (agencyLoading || (!agency?.isOperator && isLoading)) {
    return <p style={{ color: "#7C829A", padding: 24 }}>Loading…</p>;
  }
  const destination = rootLandingPath({
    isOperator: agency?.isOperator === true,
    onboardingCompleted: isError ? undefined : data?.completed,
    previewAllowed: isError ? undefined : data?.previewAllowed,
  });
  return <Navigate to={destination} replace />;
}
